/* eslint-disable max-depth */
/* eslint-disable react-func/max-combined-conditions */
import React, { useCallback, useMemo, useState } from 'react';
import { Icon } from '@fuxi/eevee-icon';
import { Dialog } from 'antd-mobile';
import { Decimal } from 'decimal.js';
import { klona } from 'klona';

import cx from './index.module.scss';

interface Props {
  direction?: 'vertical' | 'horizontal';
  label?: React.ReactNode;
  value: number;
  onChange: (value: number) => any;
  step?: number | number[];
  formatText?: (text: any) => React.ReactNode;
  max?: number;
  min?: number;
  isSubBtnDisabled?: boolean;
  disabled?: boolean;
  // 特指禁用但不在UI上展示禁用效果
  disabledForAdjust?: boolean;
  className?: string;
  suffix?: React.ReactNode;
  prefix?: React.ReactNode;
  ignoreValueDisabled?: boolean;
  propertyName?: Massage.ControlItemOptions;
  /** 是否展示筋膜宝电流设置提示 */
  isShowHotStonePowerConfirm?: boolean;
}

// 根据values生成min/max/step
export const genMinMaxSteps = (values: { value: string | number; label: string }[]) => {
  if (!values || values?.length === 0) {
    return {
      min: null,
      max: null,
      step: null,
    };
  }
  const sortedValues = klona(values)?.sort((a, b) => +a.value - +b.value);
  const step = sortedValues?.map(item => +item.value);

  return {
    min: +sortedValues[0]?.value,
    max: +sortedValues[sortedValues?.length - 1]?.value,
    step,
  };
};

const isEqual = (a: string | number, b: string | number) => {
  if (a === undefined || a === null || b === undefined || b === null || isNaN(+a) || isNaN(+b)) {
    return false;
  }
  return new Decimal(a).comparedTo(new Decimal(b)) === 0;
};
const isGreater = (a: string | number, b: string | number) => {
  if (a === undefined || a === null || b === undefined || b === null || isNaN(+a) || isNaN(+b)) {
    return false;
  }
  return new Decimal(a).comparedTo(new Decimal(b)) > 0;
};

const isLess = (a: string | number, b: string | number) => {
  if (a === undefined || a === null || b === undefined || b === null || isNaN(+a) || isNaN(+b)) {
    return false;
  }
  return new Decimal(a).comparedTo(new Decimal(b)) < 0;
};
const isGreaterOrEqual = (a: string | number, b: string | number) => isGreater(a, b) || isEqual(a, b);
const isLessOrEqual = (a: string | number, b: string | number) => isLess(a, b) || isEqual(a, b);

const Stepper: React.FC<Props> = ({
  className,
  label,
  disabled,
  disabledForAdjust,
  value,
  onChange,
  prefix,
  suffix,
  step,
  min,
  max,
  formatText,
  propertyName,
  isSubBtnDisabled = false,
  ignoreValueDisabled = false,
  isShowHotStonePowerConfirm = false,
}) => {
  const [isClickingAdd, setIsClickingAdd] = useState(false);
  const [isClickingSub, setIsClickingSub] = useState(false);

  const isSubDisabled = useMemo(
    () => isSubBtnDisabled || disabled || isLessOrEqual(value, min),
    [disabled, isSubBtnDisabled, min, value]
  );

  const isAddDisabled = useMemo(() => disabled || isGreaterOrEqual(value, max), [disabled, max, value]);

  const handleAdd = useCallback(() => {
    if (disabled || disabledForAdjust) return;

    // 当值为最大时仍然触发onChange
    if (ignoreValueDisabled && isEqual(value, max)) {
      onChange?.(value);
      return;
    }

    if (isAddDisabled) return;

    if (typeof step === 'number') {
      let newVal = value + step;
      if (max) {
        newVal = Math.min(newVal, max);
      }
      if (newVal !== value) {
        onChange?.(newVal);
      }
    } else {
      if (!step) return;

      const nextIdx = step?.findIndex(item => isEqual(item, value)) + 1;

      if (nextIdx > 0) {
        const realIdx = Math.min(step?.length - 1, nextIdx);
        const newValue = step?.[realIdx];

        if (!isEqual(newValue, value)) {
          // 展示筋膜宝电流强度条件提示
          if (propertyName === 'hot_stone.power' && isShowHotStonePowerConfirm) {
            if (newValue === 60 && value === 55) {
              Dialog.confirm({
                title: '调节须知',
                content: (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div>由于个体体感差异，</div>
                    <div style={{ color: '#0055FF', fontWeight: 'bold', margin: '2px 0' }}>
                      <span>部分顾客使用该参数酥麻感较强</span>，
                    </div>
                    <div> 调节前请和顾客沟通确认哦</div>
                  </div>
                ),
                confirmText: '确认调节',
                onConfirm: () => {
                  onChange(newValue);
                },
              });
            } else {
              onChange(newValue);
            }
          } else {
            onChange(newValue);
          }
        }
      } else {
        // 值不在枚举内时，找到与该值相比最接近的值的index
        const nearestIdx = step?.findIndex((item, index, self) => value > item && value < self[index + 1]) + 1;
        onChange(step?.[nearestIdx > 0 ? nearestIdx : 0]);
      }
    }
  }, [
    disabled,
    disabledForAdjust,
    ignoreValueDisabled,
    isAddDisabled,
    isShowHotStonePowerConfirm,
    max,
    onChange,
    propertyName,
    step,
    value,
  ]);

  const handleSub = useCallback(() => {
    if (disabled || disabledForAdjust) return;

    // 当值为最小时仍然触发onChange
    if (ignoreValueDisabled && isEqual(value, min)) {
      onChange?.(value);
      return;
    }

    if (isSubDisabled) return;

    if (typeof step === 'number') {
      let newVal = value - step;
      if (min || min === 0) {
        newVal = Math.max(newVal, min);
      }
      if (!isEqual(newVal, value)) {
        onChange?.(newVal);
      }
    } else {
      if (!step) return;

      const nextIdx = step?.findIndex(item => isEqual(item, value)) - 1;

      if (nextIdx < 0) {
        // 值不在枚举内时，找到与该值相比最接近的值的index
        const nearestIdx = step?.findIndex((item, index, self) => value > item && value < self[index + 1]);
        onChange(step?.[nearestIdx < 0 ? step?.length - 1 : nearestIdx]);
      } else {
        const realIdx = Math.max(0, nextIdx);
        if (!isEqual(step?.[realIdx], value)) {
          onChange(step?.[realIdx]);
        }
      }
    }
  }, [disabled, disabledForAdjust, ignoreValueDisabled, isSubDisabled, min, onChange, step, value]);

  return (
    <div className={cx('stepper', className, { disabled })}>
      {label && <div className={cx('label', { disabled })}>{label}</div>}
      <div
        className={cx('icon-wrapper', { disabled: isSubDisabled, active: isClickingSub })}
        onClick={handleSub}
        onTouchStart={() => !isSubDisabled && setIsClickingSub(true)}
        onTouchEnd={() => !isSubDisabled && setIsClickingSub(false)}
        onTouchCancel={() => setIsClickingSub(false)}
      >
        {propertyName === 'moxibustion.acupoint_area' ? (
          <Icon className={cx('area-icon')} name="arrow-left" />
        ) : (
          <Icon className={cx('icon')} name="sub" />
        )}
      </div>
      <div className={cx('value')}>
        <div className={cx('value-wrapper')}>
          {!!prefix && <div className={cx('prefix', { disabled })}>{prefix}</div>}
          <div className={cx('value-text-wrapper', { disabled })}>
            {formatText ? formatText(value) || value : value}
          </div>
          {!!suffix && <div className={cx('suffix', { disabled })}>{suffix}</div>}
        </div>
      </div>
      <div
        className={cx('icon-wrapper', 'icon-2', { disabled: isAddDisabled, active: isClickingAdd })}
        onClick={handleAdd}
        onTouchStart={() => !isAddDisabled && setIsClickingAdd(true)}
        onTouchEnd={() => !isAddDisabled && setIsClickingAdd(false)}
        onTouchCancel={() => setIsClickingAdd(false)}
      >
        {propertyName === 'moxibustion.acupoint_area' ? (
          <Icon className={cx('area-icon')} name="arrow-right" />
        ) : (
          <Icon className={cx('icon')} name="add" />
        )}
      </div>
    </div>
  );
};

export default Stepper;
